import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from '@mui/material';
import { Announcement } from '@core/interfaces/api';

export interface DeleteEventModalProps {
  open: boolean;
  activity: Announcement | null;
  onClose: () => void;
  onConfirm: (
    activity: Announcement,
    scope: 'single' | 'future' | 'all',
  ) => Promise<void>;
}

export function DeleteEventModal({
  open,
  activity,
  onClose,
  onConfirm,
}: DeleteEventModalProps) {
  const [deleteScope, setDeleteScope] = useState<'single' | 'future' | 'all'>(
    'single',
  );
  const [isDeleting, setIsDeleting] = useState(false);

  if (!activity) return null;

  const isRecurring = !!activity.recurrence;

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      await onConfirm(activity, deleteScope);
      onClose();
    } catch (error) {
      console.error('Error deleting activity:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setDeleteScope('single'); // Reset for next time
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete "{activity.title}"?</DialogTitle>
      <DialogContent>
        {isRecurring ? (
          <>
            <p style={{ marginTop: 0 }}>This is a recurring event.</p>
            <RadioGroup
              value={deleteScope}
              onChange={(e) =>
                setDeleteScope(e.target.value as 'single' | 'future' | 'all')
              }
            >
              <FormControlLabel
                value="single"
                control={<Radio />}
                label="Delete just this event"
                data-qa="delete-recurrence-radio-single"
              />
              <FormControlLabel
                value="future"
                control={<Radio />}
                label="Delete this and future events"
                data-qa="delete-recurrence-radio-future"
              />
              <FormControlLabel
                value="all"
                control={<Radio />}
                label="Delete all events in this series"
                data-qa="delete-recurrence-radio-all"
              />
            </RadioGroup>
          </>
        ) : (
          <p style={{ marginTop: 0 }}>This action cannot be undone.</p>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isDeleting}
          data-qa="delete-event-button-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
          data-qa="delete-event-button-confirm"
          startIcon={isDeleting ? <CircularProgress size={16} /> : null}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
